
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GET_COMPARE_REQ_API, GET_COMPLETED_REQUESTS_API } from '../config/API';
import Loader from '../components/common/Loader';
import { formatDate } from '../utils/dateUtils';
import { MdVisibility, MdSearch } from 'react-icons/md';
import './CompareAuthorizedPO.css';
import '../pages/Dashboard.css';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';

import { useUser } from '../context/UserContext';

const CompareAuthorizedPO = () => {
    const navigate = useNavigate();
    const { hasPermission } = useUser();

    const [allAuthorizedPOs, setAllAuthorizedPOs] = useState([]);
    const [completedPOs, setCompletedPOs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('Pending');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        fetchData();
    }, [activeTab, debouncedSearch, currentPage, limit]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');

            // Pick API based on active tab as per user request
            const apiToCall = activeTab === 'Pending' ? GET_COMPARE_REQ_API : GET_COMPLETED_REQUESTS_API;

            const response = await axios.get(apiToCall, {
                params: {
                    page: currentPage,
                    limit: limit,
                    search: debouncedSearch || undefined
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                const rawData = response.data.data || [];
                const formatted = groupAndFormat(rawData);

                if (activeTab === 'Pending') {
                    setAllAuthorizedPOs(formatted);
                } else {
                    setCompletedPOs(formatted);
                }
                const pagination = response.data.pagination || {};
                setTotalPages(pagination.totalPages || response.data.totalPages || 1);
                setTotalRecords(pagination.total || response.data.totalRecords || formatted.length);
            }
        } catch (error) {
            console.error('Error fetching compare data:', error);
        } finally {
            setLoading(false);
        }
    };

    const groupAndFormat = (rawData) => {
        return rawData.map(item => ({
            id: item.id,
            requestNumber: item.reqNumber || `REQ-${item.id}`,
            projectName: item.ProjectMaster?.projectName || item.projectName || '-',
            date: item.date || item.tillDate,
            status: item.status || 'Pending',
            compareStatus: item.status || 'Pending',

            // ✅ Vendor count from RequestVendors array or vendorId string
            vendorCount: Array.isArray(item.RequestVendors)
                ? item.RequestVendors.length
                : (item.vendorId ? item.vendorId.split(',').filter(Boolean).length : 0),

            // optional (if needed later)
            vendorNames: Array.isArray(item.RequestVendors)
                ? item.RequestVendors.map(v => v.Vendor?.companyName || v.Vendor?.vendorName).filter(Boolean)
                : (item.vendorName ? item.vendorName.split(',') : [])
        }));
    };

    // Filter Logic
    const displayedPOs = activeTab === 'Pending' ? allAuthorizedPOs : completedPOs;


    return (
        <div className="dashboard compare-po-page">
            <div className="page-header" >
                <h2 className="section-title">Compare Authorized POs</h2>
            </div>

            <div className={isMobile ? "" : "card-container-po-compare"}>
                {/* Search Bar */}
                <div className="search-container">
                    <TableLimitSelector limit={limit} onLimitChange={handleLimitChange} />
                    <div className="search-wrapper">
                        <MdSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search requests..."
                            className="search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className="clear-search-btn"
                                onClick={() => setSearchTerm('')}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
                {/* Tabs */}
                <div className={isMobile ? "tabs-card" : ""} style={{
                    padding: '1rem',
                    borderBottom: isMobile ? 'none' : '1px solid #e5e7eb',
                    display: 'flex',
                    gap: '1rem'
                }}>
                    <button
                        className={`tab-btn ${activeTab === 'Pending' ? 'active-tab' : ''}`}
                        onClick={() => setActiveTab('Pending')}
                        style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            background: activeTab === 'Pending' ? '#eef2ff' : 'transparent',
                            color: activeTab === 'Pending' ? '#4f46e5' : '#6b7280',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            flex: isMobile ? 1 : 'none'
                        }}
                    >
                        Pending Compare
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'Completed' ? 'active-tab' : ''}`}
                        onClick={() => setActiveTab('Completed')}
                        style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            background: activeTab === 'Completed' ? '#ecfdf5' : 'transparent',
                            color: activeTab === 'Completed' ? '#059669' : '#6b7280',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            flex: isMobile ? 1 : 'none'
                        }}
                    >
                        Completed Compare
                    </button>
                </div>
                {/* Search Bar */}


                <div className="table-container">
                    <table className="design-table">
                        <thead>
                            <tr>
                                <th>PO Number</th>
                                <th>Project Name</th>
                                <th style={{ textAlign: 'center' }}>Vendors</th>
                                <th style={{ textAlign: 'center' }}>Date</th>
                                <th style={{ textAlign: 'center' }}>Status</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6"><Loader text="Loading authorized POs..." /></td></tr>
                            ) : displayedPOs.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '3rem' }}>
                                        No authorized POs found for comparison.
                                    </td>
                                </tr>
                            ) : (
                                displayedPOs.map((po) => (
                                    <tr key={po.id}>
                                        <td>
                                            <strong className='po-number' style={{ fontFamily: 'monospace' }}>{po.requestNumber.toUpperCase()}</strong>
                                        </td>
                                        <td>{po.projectName || 'N/A'}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className="vendor-count-badge">
                                                {po.vendorCount} Vendors
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{formatDate(po.date)}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className={`status-badge status-${(po.compareStatus || 'pending').toLowerCase()}`}>
                                                {po.compareStatus}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {hasPermission('COMPARE_PO', 'read') && (
                                                <button
                                                    className="view-quote-btn"
                                                    onClick={() => navigate(`/compare-po/${po.id}`, { state: { fromTab: activeTab } })}
                                                >
                                                    <MdVisibility size={18} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile View Placeholder */}
                <div className="mobile-card-view">
                    {loading ? (
                        <Loader text="Loading..." />
                    ) : displayedPOs.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                            No authorized POs found.
                        </div>
                    ) : (
                        displayedPOs.map(po => (
                            <div key={po.id} className="dashboard-card">
                                <div className="card-header po-number" style={{ fontFamily: 'monospace' }}>
                                    <strong>{po.requestNumber.toUpperCase()}</strong>
                                    <span className={`status-badge status-${(po.compareStatus || 'pending').toLowerCase()}`}>{po.compareStatus}</span>
                                </div>
                                <div className="card-body">
                                    <div className="card-row">
                                        <span className="card-label">Project:</span>
                                        <span className="card-value">{po.projectName}</span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">Date:</span>
                                        <span className="card-value">{formatDate(po.date)}</span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">Vendors:</span>
                                        <span className="card-value">
                                            <span className="vendor-count-badge">{po.vendorCount} Vendors</span>
                                        </span>
                                    </div>
                                    <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                                        {hasPermission('COMPARE_PO', 'read') && (
                                            <button
                                                className="approve-btn-large-compare"
                                                onClick={() => navigate(`/compare-po/${po.id}`, { state: { fromTab: activeTab } })}
                                                style={{ width: '100%', justifyContent: 'center', display: 'flex', gap: '8px', padding: '10px' }}
                                            >
                                                <MdVisibility size={18} /> Compare Quotes
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
                <div className={isMobile ? "pagination-card" : ""}>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        totalRecords={totalRecords}
                        currentCount={displayedPOs.length}
                        entityName="Compare Authorized POs"
                     limit={limit} onLimitChange={handleLimitChange} />
                </div>
            </div>
        </div>
    );
};

export default CompareAuthorizedPO;
